import type { Actor, Tournament } from "@spikeeasy/tournament";
import { api, ApiError, type PendingCommand, type Snapshot } from "./api";

let database: Promise<IDBDatabase> | undefined;
function db(): Promise<IDBDatabase> {
  database ??= new Promise((resolve, reject) => {
    const request = indexedDB.open("spikeeasy-tournaments", 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore("snapshots", { keyPath: "id" });
      request.result.createObjectStore("outbox", { keyPath: "id" });
      request.result.createObjectStore("session", { keyPath: "id" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      database = undefined;
      reject(request.error);
    };
  });
  return database;
}
async function transaction<T>(
  store: string,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const database = await db();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(store, mode);
    const request = run(tx.objectStore(store));
    tx.oncomplete = () => resolve(request.result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () =>
      reject(tx.error ?? new Error("Local storage write was interrupted."));
  });
}
export async function cacheTournament(t: Tournament): Promise<void> {
  const value = structuredClone(t);
  value.teams.forEach((team) =>
    team.players.forEach((player, i) => {
      player.id = `${team.id}-${i}`;
      player.points = 0;
    }),
  );
  await transaction("snapshots", "readwrite", (store) =>
    store.put({ id: t.id, value, savedAt: Date.now() }),
  );
}
export async function cachedTournament(id: string): Promise<Tournament | null> {
  const record = await transaction("snapshots", "readonly", (store) =>
    store.get(id),
  );
  return record?.value ?? null;
}
export interface RememberedActor {
  actor: Actor | null;
  key: string | null;
}
/** Scoring rights differ per tournament, so offline reloads restore them per tournament. */
export async function rememberActor(
  tournamentId: string,
  value: RememberedActor,
): Promise<void> {
  await transaction("session", "readwrite", (store) =>
    store.put({ id: `actor:${tournamentId}`, ...value }),
  );
}
export async function rememberedActor(
  tournamentId: string,
): Promise<RememberedActor | null> {
  const record = await transaction("session", "readonly", (store) =>
    store.get(`actor:${tournamentId}`),
  );
  return record ? { actor: record.actor ?? null, key: record.key ?? null } : null;
}
export async function forgetActors(): Promise<void> {
  await transaction("session", "readwrite", (store) => store.clear());
}
export async function enqueue(command: PendingCommand): Promise<void> {
  await transaction("outbox", "readwrite", (store) => store.add(command));
}
export async function pending(): Promise<PendingCommand[]> {
  const rows = await transaction("outbox", "readonly", (store) =>
    store.getAll(),
  );
  return rows.sort((a, b) => a.createdAt - b.createdAt);
}
export async function discard(id: string): Promise<void> {
  await transaction("outbox", "readwrite", (store) => store.delete(id));
}
export async function resumeAfterLogin(actor: string): Promise<void> {
  for (const item of await pending()) {
    if (item.actor !== actor || item.errorStatus !== 401) continue;
    delete item.error;
    delete item.errorStatus;
    await transaction("outbox", "readwrite", (store) => store.put(item));
  }
}
let flushing: Promise<void> | undefined;
export async function flush(
  actor: string,
  onSaved: (snapshot: Snapshot) => void,
): Promise<void> {
  if (flushing) return flushing;
  flushing = (async () => {
    for (const item of await pending()) {
      if (item.actor !== actor) continue;
      if (item.error) break;
      try {
        const result = await api<Snapshot>(
          `/tournaments/${item.tournamentId}/commands`,
          { id: item.id, version: item.version, command: item.command },
        );
        await discard(item.id);
        onSaved(result);
      } catch (error) {
        if (
          error instanceof ApiError &&
          error.status < 500 &&
          error.status !== 429
        ) {
          item.error = error.message;
          item.errorStatus = error.status;
          await transaction("outbox", "readwrite", (store) => store.put(item));
        }
        break;
      }
    }
  })().finally(() => {
    flushing = undefined;
  });
  return flushing;
}
export function optimisticPoints(
  t: Tournament,
  items: PendingCommand[],
  actor: string,
): Tournament {
  const result = structuredClone(t);
  for (const item of items.filter(
    (item) => item.tournamentId === t.id && item.actor === actor && !item.error,
  )) {
    const command = item.command;
    if (command.type !== "point" && command.type !== "next_set") continue;
    const m = result.matches.find((m) => m.id === command.matchId);
    if (m && m.revision === command.revision) {
      if (command.type === "point")
        m.points[command.side === "a" ? 0 : 1] += command.delta;
      else {
        m.sets.push([...m.points]);
        m.points = [0, 0];
      }
      m.revision++;
    }
  }
  return result;
}
